import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";

export interface LogClientErrorInput {
  /** A stable short identifier, e.g. "card_fetch_failed". Used for filtering in admin. */
  type: string;
  /** Short human-readable summary. */
  message?: string;
  /** Optional Error.stack or anything equivalent. */
  stack?: string;
  /** Anything structured you'd want to see in the admin row. */
  context?: Record<string, unknown>;
}

/**
 * Fire-and-forget client error reporter. Never throws — if the log
 * insert itself fails we swallow the error so the caller's catch path
 * isn't hijacked by the logger.
 *
 * Writes to THREE sinks so a single outage doesn't lose the report:
 *   1. client_error_logs in Supabase (read by admin → Errors tab).
 *   2. Sentry (via captureMessage) so an outage on our own backend
 *      can't hide the error from observability.
 *   3. console.error so developers with the devtools open can see it.
 */
export async function logClientError({ type, message, stack, context }: LogClientErrorInput): Promise<void> {
  const safeContext = sanitizeContext(context);

  // Sink 1: console — synchronous, can't fail.
  try {
    console.error(`[clientError] ${type}${message ? `: ${message}` : ""}`, {
      context: safeContext,
      stack,
    });
  } catch {
    // Even console can't be trusted on exotic environments.
  }

  // Sink 2: Sentry — best-effort; Sentry.init may not have run if no DSN.
  try {
    Sentry.withScope((scope) => {
      scope.setTag("error_type", type);
      if (Object.keys(safeContext).length > 0) {
        scope.setContext("extra", safeContext as Record<string, unknown>);
      }
      Sentry.captureMessage(`${type}${message ? `: ${message}` : ""}`, "error");
    });
  } catch {
    // Sentry not wired up (no DSN) — skip.
  }

  // Sink 3: Supabase row — what the admin tab reads. May fail on RLS
  // hiccups, offline, or a missing migration; swallow any failure so
  // the caller's catch path isn't hijacked by the logger.
  try {
    let userId: string | null = null;
    let userEmail: string | null = null;
    try {
      const { data } = await supabase.auth.getUser();
      userId = data.user?.id ?? null;
      userEmail = data.user?.email ?? null;
    } catch {
      // Anonymous / auth lookup failure — still log the error itself below.
    }

    const payload = {
      user_id: userId,
      user_email: userEmail,
      error_type: type,
      message: message ?? null,
      stack: stack ?? null,
      path: typeof window !== "undefined" ? window.location.pathname : null,
      url: typeof window !== "undefined" ? window.location.href : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      context: safeContext,
    };

    await supabase.from("client_error_logs" as any).insert(payload as any);
  } catch {
    // Logging is strictly best-effort; never surface to the caller.
  }
}

/** Strip obvious non-serialisable or oversized values out of context. */
function sanitizeContext(context?: Record<string, unknown>): Record<string, unknown> {
  if (!context) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(context)) {
    if (v === undefined) continue;
    if (v instanceof Error) {
      out[k] = { name: v.name, message: v.message };
      continue;
    }
    try {
      // Round-trip JSON so functions, DOM nodes, circular refs drop out.
      JSON.stringify(v);
      out[k] = v;
    } catch {
      out[k] = String(v);
    }
  }
  return out;
}

